import React, { useMemo } from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Switch from 'react-switch';
import { InputGroup, InputLabel, TextArea } from '@components/Common';
import { formatLocalDateTime } from '@helpers/dateUtils';
import { Customer } from '@api/queries/customers';
import { entryStatus } from '@api/queries/inventory';
import { EditorMode } from '../hooks/useBookkeepingEntry';
import { BookkeepingFormData } from '../BookkeepingEntryForm';

const SwitchContainer = styled.div`
  margin: 10px 0 15px 0;
  & > div {
    margin-bottom: 10px;

    label {
      display: flex;
      flex-direction: horizontal;
      align-items: center;

      small {
        margin-left: 7px;
      }
    }
  }
`;

const StyledSwitch = styled(Switch)`
  vertical-align: middle;
  margin-right: 4px;
`;

interface Props {
  mode: EditorMode;
  isLocked: boolean;
  customers: Customer[];
}

const NotesSection: React.FC<Props> = ({ mode, isLocked, customers }) => {
  const { t } = useTranslation();
  const { control, watch, setValue } = useFormContext<BookkeepingFormData>();

  const customerId = watch('customerId');
  const entryState = watch('entryState');
  const sendToCustomer = watch('sendToCustomer');
  const lastSentToCustomer = watch('lastSentToCustomer');

  const isReadOnly = mode === 'readonly';

  // Find selected customer
  const selectedCustomer = useMemo(
    () => customers.find((c) => c.id === customerId),
    [customers, customerId]
  );

  const isEntrySendingEnabled = selectedCustomer?.isEntrySendingEnabled ?? false;

  // Handle verified switch change
  const handleVerifiedChange = (isVerified: boolean) => {
    setValue('entryState', isVerified ? entryStatus.verified : entryStatus.incomplete);
  };

  return (
    <>
      {/* Notes */}
      <InputGroup>
        <InputLabel htmlFor="notes">{t('entry.notes')}</InputLabel>
        <Controller
          name="notes"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              height="120px"
              name="notes"
              value={field.value || ''}
              onChange={(e) => setValue('notes', e.target.value)}
              disabled={isReadOnly}
            />
          )}
        />
      </InputGroup>

      {/* Billing Information */}
      <InputGroup>
        <InputLabel htmlFor="billingInformation">{t('entry.billing')}</InputLabel>
        <Controller
          name="billingInformation"
          control={control}
          render={({ field }) => (
            <TextArea
              {...field}
              height="120px"
              name="billingInformation"
              value={field.value || ''}
              onChange={(e) => setValue('billingInformation', e.target.value)}
              disabled={isReadOnly}
            />
          )}
        />
      </InputGroup>

      {/* Switches */}
      {(!isLocked || isEntrySendingEnabled) && (
        <SwitchContainer>
          {/* Verified Switch - only show when not locked */}
          {!isLocked && (
            <div>
              <label htmlFor="entry-verified">
                <StyledSwitch
                  onChange={handleVerifiedChange}
                  checked={entryState === entryStatus.verified}
                  id="entry-verified"
                />
                <span>{t('entry.states.verified')}</span>
              </label>
            </div>
          )}

          {/* Send to Customer Switch */}
          {isEntrySendingEnabled && (
            <div>
              <label htmlFor="sendToCustomer">
                <StyledSwitch
                  onChange={(isOn: boolean) => setValue('sendToCustomer', isOn)}
                  checked={sendToCustomer ?? false}
                  id="sendToCustomer"
                />
                <span>{t('entry.send_to_customer')}</span>
                {lastSentToCustomer && (
                  <small>
                    ({t('entry.last_sent')} {formatLocalDateTime(lastSentToCustomer, true)})
                  </small>
                )}
              </label>
            </div>
          )}
        </SwitchContainer>
      )}
    </>
  );
};

export default NotesSection;
